// ChartRegistryAdapter — Default implementation for chart instance registry
// Injected into VNInvestCharts to avoid globalThis.__VN_CHART_REGISTRY__
// ============================================
// Default Registry Adapter (in-memory Map)
// ============================================
export const createDefaultChartRegistryAdapter = () => {
    const registry = new Map();
    return {
        register: (id, handle) => {
            if (registry.has(id)) {
                console.warn(`ChartRegistry: Chart with id "${id}" already registered, overwriting`);
            }
            registry.set(id, handle);
        },
        unregister: (id) => {
            registry.delete(id);
        },
        get: (id) => {
            return registry.get(id);
        },
        getAll: () => {
            return Array.from(registry.values());
        },
    };
};
// Singleton - lazy initialized on first access
let _defaultChartRegistryAdapter = null;
export const getDefaultChartRegistryAdapter = () => {
    if (!_defaultChartRegistryAdapter) {
        _defaultChartRegistryAdapter = createDefaultChartRegistryAdapter();
    }
    return _defaultChartRegistryAdapter;
};
// Backward compatibility - lazy getter
export const defaultChartRegistryAdapter = new Proxy({}, {
    get(target, prop) {
        const adapter = getDefaultChartRegistryAdapter();
        return adapter[prop];
    }
});
export const createEventfulChartRegistryAdapter = () => {
    const registry = new Map();
    const registerListeners = new Set();
    const unregisterListeners = new Set();
    const changeListeners = new Set();
    const notifyChange = () => {
        const handles = Array.from(registry.values());
        changeListeners.forEach(cb => cb(handles));
    };
    return {
        register: (id, handle) => {
            registry.set(id, handle);
            registerListeners.forEach(cb => cb(id, handle));
            notifyChange();
        },
        unregister: (id) => {
            registry.delete(id);
            unregisterListeners.forEach(cb => cb(id));
            notifyChange();
        },
        get: (id) => registry.get(id),
        getAll: () => Array.from(registry.values()),
        onRegister: (callback) => {
            registerListeners.add(callback);
            return () => registerListeners.delete(callback);
        },
        onUnregister: (callback) => {
            unregisterListeners.add(callback);
            return () => unregisterListeners.delete(callback);
        },
        onChange: (callback) => {
            changeListeners.add(callback);
            return () => changeListeners.delete(callback);
        },
    };
};
// ============================================
// Persistent Registry (survives hot reload in dev)
// ============================================
/**
 * Creates a chart registry that persists across Hot Module Replacement (HMR) cycles.
 *
 * IMPORTANT: This adapter intentionally uses `globalThis` with a Symbol key to maintain
 * chart registry state during development HMR. This is a deliberate design choice for
 * developer experience - it allows charts to survive code changes without re-registration.
 *
 * This does NOT violate tree-shaking because:
 * 1. It's only used when explicitly imported (createPersistentChartRegistryAdapter)
 * 2. The default adapter (createDefaultChartRegistryAdapter) uses a local Map
 * 3. The globalThis access is inside the factory function, not at module level
 * 4. Production builds can use the non-persistent default adapter
 *
 * @returns ChartRegistryAdapter that persists across HMR
 */
export const createPersistentChartRegistryAdapter = () => {
    // Use a global symbol to persist across HMR
    // This is intentional for HMR support - see JSDoc above
    const SYMBOL_KEY = Symbol.for('vninvestcharts.chartRegistry');
    const globalAny = globalThis;
    if (!globalAny[SYMBOL_KEY]) {
        globalAny[SYMBOL_KEY] = new Map();
    }
    const registry = globalAny[SYMBOL_KEY];
    return {
        register: (id, handle) => {
            registry.set(id, handle);
        },
        unregister: (id) => {
            registry.delete(id);
        },
        get: (id) => registry.get(id),
        getAll: () => Array.from(registry.values()),
    };
};
export const createMockChartRegistryAdapter = () => {
    const registry = new Map();
    const registerCalls = [];
    const unregisterCalls = [];
    const getCalls = [];
    const adapter = {
        register: (id, handle) => {
            registerCalls.push({ id, handle });
            registry.set(id, handle);
        },
        unregister: (id) => {
            unregisterCalls.push(id);
            registry.delete(id);
        },
        get: (id) => {
            getCalls.push(id);
            return registry.get(id);
        },
        getAll: () => Array.from(registry.values()),
        registerCalls,
        unregisterCalls,
        getCalls,
        resetCalls: () => {
            registerCalls.length = 0;
            unregisterCalls.length = 0;
            getCalls.length = 0;
        },
        setMockHandles: (handles) => {
            registry.clear();
            handles.forEach((v, k) => registry.set(k, v));
        },
    };
    return adapter;
};
// ============================================
// Barrel Export
// ============================================
export * from './ResizeAdapter';
export * from './KeyboardAdapter';
export * from './ClockAdapter';
export * from './StorageAdapter';
export * from './PortalAdapter';
export * from './ChartRegistryAdapter';
